import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { formatDateOnly } from '../utils/dateOnly';

/**
 * RM1a — roster upload → preview → apply (HR › Compliance).
 *
 * THE SHAPE OF THIS SCREEN IS THE POINT. The operator uploads payroll's report
 * and sees, before anything happens, exactly which records will be updated,
 * created, made inactive, and which names the portal cannot resolve on its own.
 * Nothing is applied until they have read that and typed the number of people
 * being made inactive — which on the first run is 58, and 57 of those hold
 * signed documents.
 *
 * NOTHING HERE DELETES ANYTHING. "Inactive" hides a person from the dashboards,
 * the reports and the kiosk; their signed arbitration agreement and handbook
 * acknowledgement stay exactly where they are and stay openable from the Former
 * employees view.
 */

const BUCKETS = [
  { key: 'matched', label: 'Already on file', tone: '#4ADE80',
    blurb: 'Matched to one active record. Payroll\'s spelling and hire date will be written onto it.' },
  { key: 'created', label: 'New to the portal', tone: '#60A5FA',
    blurb: 'No active record carries this name. A new record is created; they sign at the kiosk.' },
  { key: 'ambiguous', label: 'Needs your decision', tone: '#D4A94E',
    blurb: 'The portal will not guess. Pick which record to keep — the others are made inactive, not deleted.' },
  { key: 'absent', label: 'Not on the roster', tone: '#F87171',
    blurb: 'Active here but absent from payroll\'s list. They become inactive; their documents are kept.' }
];

const RosterUpload = ({ onApplied }) => {
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [resolutions, setResolutions] = useState({});
  const [confirmText, setConfirmText] = useState('');
  const [result, setResult] = useState(null);
  const [runs, setRuns] = useState([]);
  const [openBucket, setOpenBucket] = useState('ambiguous');
  const fileRef = useRef(null);

  const fetchRuns = useCallback(async () => {
    try {
      const r = await api.get('/compliance/roster/runs');
      setRuns(r.data.runs || []);
    } catch (err) { /* the card just stays empty */ }
  }, []);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name)) {
      toast.error('The roster must be an .xlsx file');
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      setUploading(true);
      setResult(null);
      const res = await api.post('/compliance/roster/preview', form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      // Pre-load the server's suggestions so the operator adjusts rather than
      // starts from nothing — but an unresolved group still blocks Apply.
      const seed = {};
      (res.data.plan.ambiguous || []).forEach(g => {
        if (g.suggested_keeper) seed[g.key] = String(g.suggested_keeper);
      });
      setResolutions(seed);
      setConfirmText('');
      setOpenBucket(res.data.counts.ambiguous > 0 ? 'ambiguous' : 'absent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not read that roster file');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const unresolvedCount = preview
    ? (preview.plan.ambiguous || []).filter(g => !resolutions[g.key]).length
    : 0;
  // A LINK rescues someone from the absent set, so the number the operator has
  // to type moves as they make links. That is deliberate: the confirmation has
  // to be the number that will actually happen.
  const linkedIds = preview
    ? new Set(Object.entries(resolutions)
        .filter(([k, v]) => (preview.plan.created || []).some(c => c.key === k) && v && v !== 'new')
        .map(([k, v]) => Number(v)))
    : new Set();
  const linkCount = linkedIds.size;
  const inactivateCount = preview
    ? (preview.counts.absent - linkCount) + (preview.plan.ambiguous || [])
        .filter(g => g.kind === 'multiple_active')
        .reduce((n, g) => n + Math.max(0, g.candidates.length - 1), 0)
    : 0;
  const confirmOk = preview && confirmText.trim() === String(inactivateCount);

  const apply = async () => {
    if (!preview || unresolvedCount > 0 || !confirmOk) return;
    try {
      setApplying(true);
      const res = await api.post(`/compliance/roster/${preview.run_id}/apply`, { resolutions });
      setResult(res.data);
      setPreview(null);
      setResolutions({});
      setConfirmText('');
      toast.success('Roster applied');
      fetchRuns();
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Roster not applied');
    } finally {
      setApplying(false);
    }
  };

  const card = {
    background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: '12px',
    marginBottom: '24px', overflow: 'hidden'
  };
  const head = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '1px solid #2A2A2A'
  };
  const pill = (tone) => ({
    background: `${tone}22`, color: tone, border: `1px solid ${tone}55`,
    borderRadius: '999px', padding: '2px 10px', fontSize: '12px', fontWeight: 600
  });

  return (
    <div>
      {/* ---------- upload ---------- */}
      <div style={card}>
        <div style={head}>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#FFF' }}>📋 Roster reconciliation</h3>
          <div>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={onPick}
              style={{ display: 'none' }} id="roster-file" />
            <label htmlFor="roster-file" style={{
              display: 'inline-block', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer',
              background: 'rgba(212,169,78,0.12)', color: '#D4A94E', border: '1px solid #D4A94E',
              fontSize: '13px', fontWeight: 600
            }}>{uploading ? 'Reading…' : '⬆ Upload roster'}</label>
          </div>
        </div>
        <div style={{ padding: '16px 20px', fontSize: '13px', color: '#A0A0A0' }}>
          Upload payroll's Active Employee Rate Report (.xlsx). You will see exactly what
          will change before anything is applied. Records are never deleted — people who
          have left are marked inactive and keep their signed documents.
        </div>
      </div>

      {/* ---------- result ---------- */}
      {result && (
        <div style={{ ...card, border: '1px solid rgba(74,222,128,0.4)' }}>
          <div style={head}><h3 style={{ margin: 0, fontSize: '16px', color: '#4ADE80' }}>✓ Roster applied</h3></div>
          <div style={{ padding: '16px 20px', display: 'flex', gap: '28px', flexWrap: 'wrap' }}>
            <div><div style={{ fontSize: '22px', color: '#FFF' }}>{result.updated}</div>
              <div style={{ fontSize: '12px', color: '#8A8A8A' }}>records updated</div></div>
            <div><div style={{ fontSize: '22px', color: '#FFF' }}>{result.inserted}</div>
              <div style={{ fontSize: '12px', color: '#8A8A8A' }}>records created</div></div>
            <div><div style={{ fontSize: '22px', color: '#FFF' }}>{result.inactivated}</div>
              <div style={{ fontSize: '12px', color: '#8A8A8A' }}>made inactive</div></div>
            <div><div style={{ fontSize: '22px', color: '#4ADE80' }}>{result.submissions_after}</div>
              <div style={{ fontSize: '12px', color: '#8A8A8A' }}>signed documents, all kept</div></div>
          </div>
        </div>
      )}

      {/* ---------- preview ---------- */}
      {preview && (
        <div style={card}>
          <div style={head}>
            <h3 style={{ margin: 0, fontSize: '16px', color: '#FFF' }}>
              Preview — {preview.file_name}
            </h3>
            <span style={{ fontSize: '12px', color: '#8A8A8A' }}>
              {preview.counts.roster_rows} roster lines · run #{preview.run_id}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: '1px', background: '#2A2A2A' }}>
            {BUCKETS.map(b => (
              <button key={b.key} onClick={() => setOpenBucket(b.key)}
                style={{
                  background: openBucket === b.key ? '#212121' : '#1A1A1A', border: 'none',
                  padding: '16px', textAlign: 'left', cursor: 'pointer', color: 'inherit'
                }}>
                <div style={{ fontSize: '26px', fontWeight: 700, color: b.tone }}>
                  {b.key === 'absent' ? preview.counts.absent - linkCount
                    : b.key === 'created' ? preview.counts.created - linkCount
                    : preview.counts[b.key]}
                </div>
                <div style={{ fontSize: '13px', color: '#E0E0E0', fontWeight: 600 }}>{b.label}</div>
                <div style={{ fontSize: '11px', color: '#8A8A8A', marginTop: '4px', lineHeight: 1.35 }}>{b.blurb}</div>
              </button>
            ))}
          </div>

          <div style={{ padding: '16px 20px', maxHeight: '420px', overflowY: 'auto' }}>
            {openBucket === 'ambiguous' && (
              (preview.plan.ambiguous || []).length === 0
                ? <div style={{ color: '#8A8A8A', fontSize: '13px' }}>Nothing ambiguous in this roster.</div>
                : (preview.plan.ambiguous || []).map(g => (
                  <div key={g.key} style={{
                    border: '1px solid rgba(212,169,78,0.35)', borderRadius: '8px',
                    padding: '12px', marginBottom: '12px', background: 'rgba(212,169,78,0.05)'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <strong style={{ color: '#E0E0E0' }}>{g.first_name} {g.last_name}</strong>
                      <span style={pill('#D4A94E')}>{g.note}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#8A8A8A', margin: '6px 0' }}>
                      Roster: hired {formatDateOnly(g.hire_date) || '—'} · {g.department_code || 'no department code'}
                      {g.sheet_rows && g.sheet_rows.length > 1 && ` · appears on file rows ${g.sheet_rows.join(' and ')}`}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                      {g.candidates.map(c => (
                        <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#D8D8D8', cursor: 'pointer' }}>
                          <input type="radio" name={`amb-${g.key}`} value={c.id}
                            checked={String(resolutions[g.key]) === String(c.id)}
                            onChange={() => setResolutions({ ...resolutions, [g.key]: String(c.id) })} />
                          Keep #{c.id} — {c.first_name} {c.last_name}
                          <span style={{ color: '#8A8A8A' }}>
                            · hired {formatDateOnly(c.hire_date) || '—'}
                            · {c.completed_docs} signed document{c.completed_docs === 1 ? '' : 's'}
                            {c.is_manual_entry ? ' · added at the kiosk' : ''}
                          </span>
                        </label>
                      ))}
                      {g.kind === 'roster_repeat' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#D8D8D8', cursor: 'pointer' }}>
                          <input type="radio" name={`amb-${g.key}`} value="new"
                            checked={resolutions[g.key] === 'new'}
                            onChange={() => setResolutions({ ...resolutions, [g.key]: 'new' })} />
                          These are two different people — create a new record
                        </label>
                      )}
                    </div>
                  </div>
                ))
            )}

            {openBucket === 'absent' && (
              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                <thead><tr style={{ color: '#8A8A8A', textAlign: 'left' }}>
                  <th style={{ padding: '6px 4px' }}>Name</th><th>Hired</th><th>Signed documents</th><th>Source</th>
                </tr></thead>
                <tbody>
                  {(preview.plan.absent || []).map(a => {
                    const rescued = linkedIds.has(a.id);
                    return (
                      <tr key={a.id} style={{ borderTop: '1px solid #2A2A2A', color: rescued ? '#60A5FA' : '#D8D8D8', opacity: rescued ? 0.85 : 1 }}>
                        <td style={{ padding: '6px 4px', textDecoration: rescued ? 'line-through' : 'none' }}>{a.first_name} {a.last_name}</td>
                        <td>{formatDateOnly(a.hire_date) || '—'}</td>
                        <td style={{ color: rescued ? '#60A5FA' : (a.completed_docs > 0 ? '#4ADE80' : '#8A8A8A') }}>{a.completed_docs}</td>
                        <td style={{ color: '#8A8A8A' }}>
                          {rescued ? 'staying — linked to a roster line' : (a.is_manual_entry ? 'kiosk' : 'roster')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {openBucket === 'created' && (
              <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                <thead><tr style={{ color: '#8A8A8A', textAlign: 'left' }}>
                  <th style={{ padding: '6px 4px' }}>Name</th><th>Hired</th><th>Department</th><th>Note</th>
                </tr></thead>
                <tbody>
                  {(preview.plan.created || []).map(c => (
                    <tr key={c.key} style={{ borderTop: '1px solid #2A2A2A', color: '#D8D8D8' }}>
                      <td style={{ padding: '6px 4px' }}>{c.first_name} {c.last_name}</td>
                      <td>{formatDateOnly(c.hire_date) || '—'}</td>
                      <td style={{ color: '#8A8A8A' }}>{c.department_code || '—'}</td>
                      <td style={{ fontSize: '12px' }}>
                        {c.rehire && <span style={pill('#D4A94E')}>rehire — will sign again</span>}
                        {(c.hints || []).length === 0 && !c.rehire && (
                          <span style={{ color: '#6B6B6B' }}>—</span>
                        )}
                        {/* LINK PICKER. Only offered where the portal found a
                            plausible existing record, and only ever offered —
                            the default is always "new employee". Choosing a
                            link turns a create + an inactivation into a single
                            rename, so the person keeps the record holding their
                            signed documents. */}
                        {(c.hints || []).length > 0 && (
                          <select
                            value={resolutions[c.key] || 'new'}
                            onChange={e => setResolutions({ ...resolutions, [c.key]: e.target.value })}
                            style={{
                              background: '#0E0E0E', color: resolutions[c.key] && resolutions[c.key] !== 'new' ? '#60A5FA' : '#A0A0A0',
                              border: '1px solid #2A2A2A', borderRadius: '6px', padding: '4px 6px', fontSize: '12px', maxWidth: '320px'
                            }}>
                            <option value="new">No, a new employee</option>
                            {(c.hints || []).map(h => (
                              <option key={h.id} value={String(h.id)}>
                                Same person as {h.name} (#{h.id})
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {openBucket === 'matched' && (
              <>
                <div style={{ fontSize: '12px', color: '#8A8A8A', marginBottom: '8px' }}>
                  {preview.counts.matched_with_changes} of {preview.counts.matched} will actually change;
                  the rest already agree with payroll.
                </div>
                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ color: '#8A8A8A', textAlign: 'left' }}>
                    <th style={{ padding: '6px 4px' }}>Name</th><th>Department</th><th>Changes</th>
                  </tr></thead>
                  <tbody>
                    {(preview.plan.matched || []).filter(m => m.has_changes).map(m => (
                      <tr key={m.id} style={{ borderTop: '1px solid #2A2A2A', color: '#D8D8D8' }}>
                        <td style={{ padding: '6px 4px' }}>{m.first_name} {m.last_name}</td>
                        <td style={{ color: '#8A8A8A' }}>{m.department_code || '—'}</td>
                        <td style={{ fontSize: '12px', color: '#8A8A8A' }}>
                          {Object.entries(m.changes).map(([f, v]) => (
                            <div key={f}>{f.replace('_', ' ')}: {String(v.from || '—')} → {String(v.to || '—')}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* ---------- apply ---------- */}
          <div style={{ padding: '16px 20px', borderTop: '1px solid #2A2A2A', background: '#151515' }}>
            {unresolvedCount > 0 && (
              <div style={{ color: '#D4A94E', fontSize: '13px', marginBottom: '10px' }}>
                {unresolvedCount} name{unresolvedCount === 1 ? '' : 's'} still need a decision above.
              </div>
            )}
            {linkCount > 0 && (
              <div style={{ color: '#60A5FA', fontSize: '13px', marginBottom: '10px' }}>
                {linkCount} roster line{linkCount === 1 ? '' : 's'} linked to an existing record — {linkCount === 1 ? 'that person keeps' : 'those people keep'} their signed documents and {linkCount === 1 ? 'is' : 'are'} no longer being made inactive.
              </div>
            )}
            <div style={{ fontSize: '13px', color: '#D8D8D8', marginBottom: '8px' }}>
              This will make <strong style={{ color: '#F87171' }}>{inactivateCount}</strong> {inactivateCount === 1 ? 'person' : 'people'} inactive
              {preview.counts.absent_with_documents > 0 && (
                <> — {preview.counts.absent_with_documents} of them hold signed documents, which are kept</>
              )}. Type <strong>{inactivateCount}</strong> to confirm.
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <input value={confirmText} onChange={e => setConfirmText(e.target.value)}
                placeholder={String(inactivateCount)} style={{
                  width: '90px', padding: '8px', borderRadius: '6px',
                  background: '#0E0E0E', border: '1px solid #2A2A2A', color: '#E0E0E0'
                }} />
              <button disabled={applying || unresolvedCount > 0 || !confirmOk} onClick={apply}
                style={{
                  padding: '9px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                  cursor: (applying || unresolvedCount > 0 || !confirmOk) ? 'not-allowed' : 'pointer',
                  background: (unresolvedCount > 0 || !confirmOk) ? 'rgba(255,255,255,0.06)' : 'rgba(74,222,128,0.15)',
                  color: (unresolvedCount > 0 || !confirmOk) ? '#6B6B6B' : '#4ADE80',
                  border: `1px solid ${(unresolvedCount > 0 || !confirmOk) ? '#2A2A2A' : 'rgba(74,222,128,0.4)'}`
                }}>{applying ? 'Applying…' : 'Apply roster'}</button>
              <button onClick={() => { setPreview(null); setResolutions({}); setConfirmText(''); }}
                style={{
                  padding: '9px 16px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
                  background: 'transparent', color: '#A0A0A0', border: '1px solid #2A2A2A'
                }}>Discard</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- history ---------- */}
      {runs.length > 0 && (
        <div style={card}>
          <div style={head}><h3 style={{ margin: 0, fontSize: '16px', color: '#FFF' }}>Roster history</h3></div>
          <div style={{ padding: '8px 20px 16px' }}>
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
              <thead><tr style={{ color: '#8A8A8A', textAlign: 'left' }}>
                <th style={{ padding: '6px 4px' }}>Run</th><th>File</th><th>Status</th>
                <th>Updated</th><th>Created</th><th>Inactive</th><th>By</th>
              </tr></thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} style={{ borderTop: '1px solid #2A2A2A', color: '#D8D8D8' }}>
                    <td style={{ padding: '6px 4px' }}>#{r.id}</td>
                    <td style={{ color: '#8A8A8A' }}>{r.file_name}</td>
                    <td><span style={pill(r.status === 'applied' ? '#4ADE80' : r.status === 'previewed' ? '#D4A94E' : '#6B6B6B')}>{r.status}</span></td>
                    <td>{r.counts?.updated ?? '—'}</td>
                    <td>{r.counts?.inserted ?? '—'}</td>
                    <td>{r.counts?.inactivated ?? '—'}</td>
                    <td style={{ color: '#8A8A8A' }}>{r.applied_by_name || r.uploaded_by_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default RosterUpload;
